import React, { useMemo } from 'react';
import { getCoreRowModel, useReactTable, type ColumnDef, type Table } from '@tanstack/react-table';
import { Card, Checkbox, TabStrip, alignEnd, headerEnd, tableView, unpaginated } from '../../components/ui';
import { ProductFamily, ProductFamilySelection } from '../../types';
import { AppTable, AppTableHorizontalScrollControls, Button, InputNumber, InputSwitch, StatusTag, Tooltip, useAppTableHorizontalScroll } from '@allygpo/ally-ux';

export interface MixStatus {
  category: string;
  /** Proposed total of the included families, 0–1. */
  total: number;
  included: number;
  valid: boolean;
  /** The user turned the proposed mix on for this area: it's modeled and must total 100%. */
  enabled: boolean;
}

interface ProductMixCardProps {
  categories: string[];
  activeCategory: string | null;
  /** The active area's families, in the fixed (alphabetical) order. */
  families: ProductFamily[];
  selections: Record<string, ProductFamilySelection>;
  proposedMixByCategory: Record<string, Record<string, number>>;
  statusByCategory: Record<string, MixStatus>;
  showOverrides: boolean;
  onToggleOverrides: (next: boolean) => void;
  /** The active area's proposed mix is modeled as its own scenario. */
  proposedEnabled: boolean;
  onToggleProposed: (next: boolean) => void;
  canReset: boolean;
  onReset: () => void;
  onCategoryChange: (category: string) => void;
  onIncludeChange: (family: ProductFamily, include: boolean) => void;
  /** Check or uncheck every product in the active area. */
  onIncludeAll: (include: boolean) => void;
  onProposedMixChange: (family: ProductFamily, value: number) => void;
  onMinShareChange: (family: ProductFamily, value: number) => void;
  onDirectRebateChange: (family: ProductFamily, value: number) => void;
  onAdminFeeChange: (family: ProductFamily, value: number) => void;
}

const asPercent = (value: number): number => Math.round(value * 10000) / 100;
const pctText = (value: number) => `${asPercent(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;

/** "Proposed mix off" / "Ready" / "12 pts to add" / "3 pts over" / "Select at least two products". */
export const mixStatusMessage = (status: MixStatus | undefined): string => {
  if (!status) return '';
  if (status.included < 2) return 'Select at least two products';
  if (!status.enabled) return 'Proposed mix off';
  if (status.valid) return 'Ready';
  const diff = Math.round(Math.abs(1 - status.total) * 10000) / 100;
  return status.total < 1 ? `${diff} pts to add` : `${diff} pts over`;
};

/** Muted while the proposed mix is off (nothing to check), else green when ready, red when not. */
const statusTone = (status: MixStatus | undefined): string => {
  if (status && !status.enabled && status.included >= 2) return ' is-unedited';
  return status?.valid ? ' siq-pos text-success' : ' siq-neg text-danger';
};

const purchasedThisQuarter = (family: ProductFamily) =>
  (family.product_family_to_category_qtd_ratio ?? 0) > 0
  || (family.product_family_to_category_spend_qtd_ratio ?? 0) > 0
  || (family.product_family_to_category_invoice_units_qtd_ratio ?? 0) > 0;

// Workaround for a PrimeReact InputNumber bug (mode="currency"): backspacing the
// currency symbol ("$") corrupts the value (e.g. $100 -> 10100) because its internal
// handler treats the missing minus sign (index -1) as adjacent to the currency symbol
// (index 0). Block Backspace only when the char before the caret is a non-numeric
// symbol like "$"; digits, separators, decimal point and the minus sign are untouched.
const blockCurrencySymbolBackspace = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key !== 'Backspace') return;
  const input = e.target as HTMLInputElement;
  const { selectionStart, selectionEnd, value } = input;
  if (selectionStart === null || selectionStart !== selectionEnd) return; // allow range deletes
  const charBefore = value.charAt(selectionStart - 1);
  if (charBefore && /[^0-9.,\-\s]/.test(charBefore)) {
    e.preventDefault();
  }
};

/** A product row, or the Total row that closes the table (the mix keeps a fixed order, so it stays last). */
type MixRow = { kind: 'family'; family: ProductFamily } | { kind: 'total' };

/** What the Product mix cells read from `meta.view`. */
interface MixView {
  activeCategory: string;
  families: ProductFamily[];
  selections: Record<string, ProductFamilySelection>;
  proposedMixByCategory: Record<string, Record<string, number>>;
  status: MixStatus | undefined;
  proposedEnabled: boolean;
  currentTotal: number;
  costTotal: number;
  allIncluded: boolean;
  someIncluded: boolean;
  onIncludeChange: (family: ProductFamily, include: boolean) => void;
  onIncludeAll: (include: boolean) => void;
  onProposedMixChange: (family: ProductFamily, value: number) => void;
  onMinShareChange: (family: ProductFamily, value: number) => void;
  onDirectRebateChange: (family: ProductFamily, value: number) => void;
  onAdminFeeChange: (family: ProductFamily, value: number) => void;
}

const mixView = (table: Table<MixRow>) => tableView<MixView, MixRow>(table);
const isIncluded = (view: MixView, family: ProductFamily) => view.selections[family.product_family_name]?.include ?? false;

/** Header label with an info icon; the card's Tooltip shows the hint. */
const infoHeader = (label: string, hint: string, end = false) => {
  const content = <>{label}<i className="pi pi-info-circle mix-head-info u-ml-1 text-2xs text-tertiary" data-pr-tooltip={hint} /></>;
  return end ? headerEnd(content) : content;
};

const mixColumns: ColumnDef<MixRow>[] = [
  {
    id: 'use',
    size: 48,
    header: ({ table }) => {
      const { families, allIncluded, someIncluded, onIncludeAll } = mixView(table);
      return (
        <span className="mix-center flex justify-content-center text-center">
          <Checkbox
            checked={allIncluded}
            indeterminate={someIncluded}
            onChange={() => onIncludeAll(!allIncluded)}
            ariaLabel={allIncluded ? 'Uncheck all products' : 'Check all products'}
            disabled={families.length === 0}
          />
        </span>
      );
    },
    cell: ({ row: { original: row }, table }) => {
      if (row.kind === 'total') return null;
      const view = mixView(table);
      return (
        <span className="mix-center flex justify-content-center text-center">
          <Checkbox checked={isIncluded(view, row.family)} onChange={(next) => view.onIncludeChange(row.family, next)} ariaLabel={`Use ${row.family.product_family_name}`} />
        </span>
      );
    },
  },
  {
    id: 'product',
    size: 180,
    header: 'Product',
    // The total's status sits with its label, so the numeric columns hold numbers only.
    cell: ({ row: { original: row }, table }) => {
      if (row.kind === 'total') {
        const { status } = mixView(table);
        return <span>Total<span className={`mix-total-msg u-ml-1 text-xs font-normal${statusTone(status)}`}>· {mixStatusMessage(status)}</span></span>;
      }
      return (
        <span className="flex align-items-center u-gap-2">
          <span className="font-medium">{row.family.product_family_name}</span>
          {!purchasedThisQuarter(row.family) && <StatusTag variant="neutral">Not purchased</StatusTag>}
        </span>
      );
    },
  },
  {
    id: 'unitEquiv',
    size: 90,
    header: () => headerEnd('Unit equiv.'),
    meta: alignEnd,
    cell: ({ row: { original: row } }) => (row.kind === 'total' ? null : row.family.display_unit_equivalency || '—'),
  },
  {
    id: 'current',
    size: 95,
    header: () => infoHeader('Current mix', 'Unit-based distribution used for scenario generation', true),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => pctText(row.kind === 'total' ? mixView(table).currentTotal : row.family.current_distribution ?? 0),
  },
  {
    id: 'cost',
    size: 95,
    header: () => infoHeader('Cost mix', 'Spend allocation across drugs, does not affect scenarios', true),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => pctText(row.kind === 'total' ? mixView(table).costTotal : row.family.current_spend_distribution ?? 0),
  },
  {
    id: 'proposed',
    size: 120,
    header: () => headerEnd('Proposed mix'),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => {
      const view = mixView(table);
      // The total sits where the typed values do, right-aligned under them.
      if (row.kind === 'total') return <span className={`block text-right mix-total u-pr-3${statusTone(view.status)}`}>{pctText(view.status?.total ?? 0)}</span>;
      const { family } = row;
      const on = isIncluded(view, family);
      const proposed = view.proposedMixByCategory[view.activeCategory]?.[family.product_family_id]
        ?? view.selections[family.product_family_name]?.proposedDistribution
        ?? family.current_distribution
        ?? 0;
      // While the proposed mix is off, its column reads as today's mix carried over, not an input in use.
      return (
        <span className="block mix-input is-pct">
          <InputNumber
            suffix="%"
            inputClassName={`w-full text-right${view.proposedEnabled ? '' : ' text-tertiary'}`}
            value={on ? asPercent(proposed) : 0}
            min={0} max={100} useGrouping={false} minFractionDigits={0} maxFractionDigits={2}
            disabled={!on || !view.proposedEnabled}
            onValueChange={(e) => view.onProposedMixChange(family, (e.value ?? 0) / 100)}
            aria-label={`Proposed mix for ${family.product_family_name}`}
          />
        </span>
      );
    },
  },
  {
    id: 'minShare',
    size: 120,
    header: () => headerEnd('Min share'),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => {
      const view = mixView(table);
      if (row.kind === 'total') return null;
      const { family } = row;
      const on = isIncluded(view, family);
      return (
        <span className="block mix-input is-pct">
          <InputNumber
            suffix="%"
            inputClassName="w-full text-right"
            value={on ? asPercent(view.selections[family.product_family_name]?.minDistribution ?? 0) : 0}
            min={0} max={100} useGrouping={false} minFractionDigits={0} maxFractionDigits={2}
            disabled={!on}
            onValueChange={(e) => view.onMinShareChange(family, (e.value ?? 0) / 100)}
            aria-label={`Minimum share for ${family.product_family_name}`}
          />
        </span>
      );
    },
  },
  {
    id: 'directRebate',
    size: 120,
    header: () => infoHeader('Direct rebate / EU', 'A flat-dollar rebate earned on 1 equivalent-unit dose that is paid directly, outside of any contract opportunity. This rebate is added on top of contract rebates when calculating net cost recovery.'),
    cell: ({ row: { original: row }, table }) => {
      if (row.kind === 'total') return null;
      const view = mixView(table);
      const { family } = row;
      const on = isIncluded(view, family);
      // Positive-only, so min is 0 rather than the admin fee's -9999 (a negative admin fee is a real net cost).
      return (
        <span className="block mix-input">
          <InputNumber
            inputClassName="w-full text-right"
            value={on ? (view.selections[family.product_family_name]?.directRebatePerEu ?? null) : null}
            mode="currency" currency="USD" locale="en-US" min={0} max={9999} minFractionDigits={0} maxFractionDigits={2}
            placeholder="$0.00" disabled={!on} onKeyDown={blockCurrencySymbolBackspace}
            onValueChange={(e) => view.onDirectRebateChange(family, Math.max(0, e.value ?? 0))}
            aria-label={`Direct rebate per EU for ${family.product_family_name}`}
          />
        </span>
      );
    },
  },
  {
    id: 'adminFee',
    size: 120,
    header: () => infoHeader('Admin fee / EU', 'A flat-dollar estimate of the reimbursement, net of cost, associated with administering of 1 equivalent-unit dose to the patient. This fee is added on top of the drug reimbursement when calculating net cost recovery.'),
    cell: ({ row: { original: row }, table }) => {
      if (row.kind === 'total') return null;
      const view = mixView(table);
      const { family } = row;
      const on = isIncluded(view, family);
      return (
        <span className="block mix-input">
          <InputNumber
            inputClassName="w-full text-right"
            value={on ? (view.selections[family.product_family_name]?.adminFeePerEu ?? null) : null}
            mode="currency" currency="USD" locale="en-US" min={-9999} max={9999} minFractionDigits={0} maxFractionDigits={2}
            placeholder="$0.00" disabled={!on} onKeyDown={blockCurrencySymbolBackspace}
            onValueChange={(e) => view.onAdminFeeChange(family, e.value ?? 0)}
            aria-label={`Admin fee per EU for ${family.product_family_name}`}
          />
        </span>
      );
    },
  },
];

/** The mix table, its own component so the horizontal-scroll hook runs once the table is mounted. */
const MixTable: React.FC<{ table: Table<MixRow>; activeCategory: string; footerSlot?: React.ReactNode }> = ({ table, activeCategory, footerSlot }) => {
  const horizontalScroll = useAppTableHorizontalScroll();
  return (
    <AppTable
      isBordered={false}
      size="sm"
      className="siq-dense-inputs"
      ariaLabel={`Product mix for ${activeCategory}`}
      table={table}
      horizontalScroll={horizontalScroll}
      headerSlot={horizontalScroll.isOverflowingHorizontally ? <AppTableHorizontalScrollControls horizontalScroll={horizontalScroll} /> : undefined}
      rowClassName={({ rowData }) => (rowData.kind === 'total' ? 'is-total' : isIncluded(mixView(table), rowData.family) ? '' : 'is-off')}
      footerSlot={footerSlot}
    />
  );
};

/**
 * Product mix for the run, one area at a time (tabs when several areas are selected, each
 * with its own 100% status). The proposed mix is modeled exactly as a PROPOSED scenario;
 * Min share stays a separate generator constraint.
 */
const ProductMixCard: React.FC<ProductMixCardProps> = ({
  categories, activeCategory, families, selections, proposedMixByCategory, statusByCategory,
  showOverrides, onToggleOverrides, proposedEnabled, onToggleProposed, canReset, onReset, onCategoryChange,
  onIncludeChange, onIncludeAll, onProposedMixChange, onMinShareChange, onDirectRebateChange, onAdminFeeChange,
}) => {
  const status = activeCategory ? statusByCategory[activeCategory] : undefined;
  const included = families.filter((family) => selections[family.product_family_name]?.include);
  const allIncluded = families.length > 0 && included.length === families.length;
  const hasTotal = included.length > 0;
  const rows = useMemo<MixRow[]>(() => [
    ...families.map((family) => ({ kind: 'family' as const, family })),
    ...(hasTotal ? [{ kind: 'total' as const }] : []),
  ], [families, hasTotal]);
  const table = useReactTable({
    ...unpaginated,
    data: rows,
    columns: mixColumns,
    meta: {
      view: {
        activeCategory: activeCategory ?? '',
        families,
        selections,
        proposedMixByCategory,
        status,
        proposedEnabled,
        currentTotal: families.reduce((sum, f) => sum + (f.current_distribution ?? 0), 0),
        costTotal: families.reduce((sum, f) => sum + (f.current_spend_distribution ?? 0), 0),
        allIncluded,
        someIncluded: included.length > 0 && !allIncluded,
        onIncludeChange,
        onIncludeAll,
        onProposedMixChange,
        onMinShareChange,
        onDirectRebateChange,
        onAdminFeeChange,
      } satisfies MixView,
    },
    state: { columnVisibility: { directRebate: showOverrides, adminFee: showOverrides } },
    getRowId: (row) => (row.kind === 'total' ? 'total' : row.family.product_family_id),
    getCoreRowModel: getCoreRowModel(),
  });

  // Nothing to show until an area is chosen; the area field above already asks for one.
  if (!categories.length || !activeCategory) return null;

  return (
    <Card
      className="mix-card"
      title={`Product mix · ${activeCategory}`}
      subtitle="Check the products to include. Turn on Proposed mix to model your own mix as a scenario; it must total 100%."
      actions={<>
        <label className="inline-flex align-items-center u-gap-2 u-pr-2 text-xs text-secondary cursor-pointer">
          <InputSwitch inputId="toggle-proposed-mix" checked={proposedEnabled} onChange={(e) => onToggleProposed(!!e.value)} />
          <span>Proposed mix</span>
        </label>
        <label className="inline-flex align-items-center u-gap-2 u-pr-2 text-xs text-secondary cursor-pointer">
          <InputSwitch inputId="toggle-overrides" checked={showOverrides} onChange={(e) => onToggleOverrides(!!e.value)} />
          <span>Rebate &amp; fee overrides</span>
        </label>
        {canReset && <Button variant="primary-text" onClick={onReset}>Reset to current</Button>}
      </>}
    >
      <Tooltip target=".mix-head-info" position="top" />

      {categories.length > 1 && (
        <TabStrip
          className="mix-tabs u-py-0 u-px-4 border-b-1 border-default"
          tabs={categories.map((category) => {
            const s = statusByCategory[category];
            return {
              key: category,
              header: <>{category}<span className={`mix-tab-status u-ml-2 text-2xs font-semibold${statusTone(s)}`}>{s?.valid ? '100%' : pctText(s?.total ?? 0)}</span></>,
            };
          })}
          active={activeCategory}
          onChange={onCategoryChange}
        />
      )}

      <MixTable
        table={table}
        activeCategory={activeCategory}
        footerSlot={included.length === 0 ? <span className="siq-dim text-tertiary">No products selected. Check at least two products to build a mix.</span> : undefined}
      />
    </Card>
  );
};

export default ProductMixCard;
